import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '../../ui/card'
import { Button } from '../../ui/button'
import { supabase } from '../../../lib/supabase'
import { toast } from 'sonner'
import { Modal } from '../../ui/modal'
import { Loader2, Users, Calendar, Code } from 'lucide-react'
import { debug } from '../../../lib/debug'
import { SummarySection } from './summary-section'
import { ObservationsSection } from './observations-section'
import { RoleInsightsSection } from './role-insights-section'
import { RecommendationsSection } from './recommendations-section'
import { AlignmentSection } from './alignment-section'
import { SpectrumSection } from './spectrum-section'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import { trackEvent, EventCategory } from '../../../lib/analytics'
import type { SurveyInsight } from '../../../lib/types/insights'

interface SurveyInsightsViewProps {
  surveyCode: string
}

export function SurveyInsightsView({ surveyCode }: SurveyInsightsViewProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [insights, setInsights] = useState<SurveyInsight[]>([])
  const [selectedInsight, setSelectedInsight] = useState<SurveyInsight | null>(null)
  const [showVersionSelector, setShowVersionSelector] = useState(false)
  const [showRawData, setShowRawData] = useState(false)

  useEffect(() => {
    loadInsights()
  }, [surveyCode])

  const loadInsights = async () => {
    try {
      const { data, error } = await supabase
        .from('survey_insights')
        .select('*')
        .eq('survey_code', surveyCode)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Parse insights JSON if needed
      const parsedInsights = data?.map(insight => ({
        ...insight,
        insights: typeof insight.insights === 'string' 
          ? JSON.parse(insight.insights) 
          : insight.insights
      })) || []

      setInsights(parsedInsights)

      // Select the most recent insight by default
      if (parsedInsights.length > 0) {
        setSelectedInsight(parsedInsights[0])
      }
    } catch (error) {
      console.error('Error loading insights:', error)
      toast.error('Failed to load insights')
    } finally {
      setIsLoading(false)
    }
  }

  const handleTabChange = (tab: string) => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: `insights_tab_${tab}`
    })
  }

  const handleVersionSelect = (insight: SurveyInsight) => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: 'insights_version_select'
    })
    setSelectedInsight(insight)
    setShowVersionSelector(false)
  }

  const toggleRawData = () => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: showRawData ? 'hide_raw_data' : 'show_raw_data'
    })
    setShowRawData(!showRawData)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!insights.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">No insights have been generated for this survey yet.</p>
          <p className="text-muted-foreground mt-2">Process the survey results to generate insights.</p>
        </CardContent>
      </Card>
    )
  }

  if (!selectedInsight?.insights) {
    return null
  }

  // Get properties from insights using optional chaining and provide defaults
  const {
    summary,
    strengths,
    challenges,
    recommendations = [],
    spectrumAnalysis,
    roleBasedInsights
  } = selectedInsight.insights
  
  // These properties might not exist in the insights structure
  const consolidatedSummary = (selectedInsight.insights as any).consolidatedSummary || summary?.overview
  const keyObservations = (selectedInsight.insights as any).keyObservations || summary?.keyThemes
  const roleInsights = (selectedInsight.insights as any).roleInsights || roleBasedInsights
  const alignmentOpportunities = (selectedInsight.insights as any).alignmentOpportunities || []

  // Capitalize priority values in recommendations to match the expected type
  const formattedRecommendations = recommendations.map(rec => ({
    ...rec,
    priority: rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1).toLowerCase() as 'High' | 'Medium' | 'Low'
  }))

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={() => setShowVersionSelector(true)}
        >
          Select Version
        </Button>
        <Button
          variant="outline"
          onClick={toggleRawData}
          className="flex items-center gap-2"
        >
          <Code className="w-4 h-4" />
          {showRawData ? 'Show Structured View' : 'Show Raw Data'}
        </Button>
      </div>

      {showRawData ? (
        <Card>
          <CardContent className="p-6">
            <pre className="whitespace-pre-wrap overflow-auto max-h-[800px] text-sm">
              {JSON.stringify(selectedInsight.insights, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="summary" className="space-y-8" onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="w-full flex-wrap justify-start h-auto p-1 gap-1">
              <TabsTrigger 
                value="summary" 
                className="text-sm px-3 py-1.5 h-auto data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Summary
              </TabsTrigger>
              <TabsTrigger 
                value="roles" 
                className="text-sm px-3 py-1.5 h-auto data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Roles
              </TabsTrigger>
              <TabsTrigger 
                value="alignment" 
                className="text-sm px-3 py-1.5 h-auto data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Alignment
              </TabsTrigger>
              <TabsTrigger 
                value="recommendations" 
                className="text-sm px-3 py-1.5 h-auto data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Recommendations
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="summary" className="space-y-8">
            {consolidatedSummary && (
              <SummarySection 
                overview={consolidatedSummary.overview}
                keyFindings={consolidatedSummary.keyFindings}
                participationStats={{
                  roles: consolidatedSummary.participationStats?.roles || []
                }}
              />
            )}

            {keyObservations && (
              <ObservationsSection 
                leadershipPerceptions={keyObservations.leadershipPerceptions || []}
                variabilityNotes={keyObservations.variabilityNotes || []}
              />
            )}
          </TabsContent>

          <TabsContent value="roles">
            {roleInsights && (
              <RoleInsightsSection 
                insights={roleInsights}
              />
            )}
          </TabsContent>

          <TabsContent value="alignment" className="space-y-8">
            {spectrumAnalysis && (
              <SpectrumSection 
                spectra={Object.entries(spectrumAnalysis).map(([name, data]) => ({
                  name: name.replace(/([A-Z])/g, ' $1').trim(),
                  currentCommentary: data.currentState,
                  aspirationalCommentary: data.desiredState,
                  alignmentOpportunities: data.gap
                }))}
              />
            )}

            {alignmentOpportunities && (
              <AlignmentSection 
                opportunities={alignmentOpportunities}
              />
            )}
          </TabsContent>

          <TabsContent value="recommendations">
            {formattedRecommendations && (
              <RecommendationsSection 
                recommendations={formattedRecommendations} 
              />
            )}
          </TabsContent>
        </Tabs>
      )}

      <Modal
        open={showVersionSelector}
        onClose={() => setShowVersionSelector(false)}
        title="Select Insights Version"
      >
        <div className="space-y-4">
          {insights.map((insight) => (
            <button
              key={insight.id}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                selectedInsight?.id === insight.id 
                  ? 'border-primary bg-primary/5' 
                  : 'hover:border-primary/50'
              }`}
              onClick={() => handleVersionSelect(insight)}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4">
                  <span>
                    Generated on {new Date(insight.created_at).toLocaleDateString()} at{' '}
                    {new Date(insight.created_at).toLocaleTimeString()}
                  </span>
                </div>
                {insight.metadata && (
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    {insight.metadata.total_responses !== undefined && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {insight.metadata.total_responses} responses
                      </div>
                    )}
                    {insight.metadata.roles?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {insight.metadata.roles.length} unique roles
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  )
}